import isEqual from "lodash/isEqual";
import groupBy from "lodash/groupBy";
import qs from "query-string";
import { getAllSizes } from "../productListingFiltersWeb/filterSizes/utils";
import {
  CATEGORY_QUERY_PARAMS,
  FILTER_QUERY_PARAMS,
  FILTER_TYPES
} from "../constants";

export {
  dropShallowRowsCoveredByDeeper,
  stripCategoryDeeperThanAnchor
} from "./pruneOrphanedSizeSelections";

export { CATEGORY_QUERY_PARAMS } from "../constants";
import {
  Facet,
  FilterOption,
  FilterSizeVariants,
  SelectedFiltersType
} from "../types";

export type BuildPlpQueryFromFilterSelectionOptions = {
  filterQueryParamKeys: Set<string>;
  touchedQueryParamKeys: Set<string>;
  fullFilterReplace?: boolean;
};

type SizeCategory = {
  label: string;
  defaultLabel: string;
  parentCategoryKey: string;
  data: {
    simple: unknown[];
    advanced: unknown[];
  };
};

type FacetBase = {
  queryParam: string;
  type: string;
  label?: string;
  defaultLabel?: string;
  [key: string]: unknown;
};

type SizeFacetAggregated = FacetBase & {
  queryParam: "size";
  type: "size";
  data: SizeCategory[];
};

type SizeFacetSplit = FacetBase & {
  queryParam: "size";
  type: "size";
  label: string;
  defaultLabel: string;
  parentCategoryKey: string;
  data: SizeCategory["data"];
};

export const splitSizeFacetInFacets = (facets: FacetBase[]): FacetBase[] => {
  const idx = facets.findIndex(f => f?.queryParam === "size");
  if (idx === -1) return facets;

  const facet = facets[idx] as SizeFacetAggregated;

  if (!Array.isArray((facet as any).data)) return facets;

  const categories = (facet.data ?? []) as SizeCategory[];

  const {
    data: _data,
    label: _label,
    defaultLabel: _defaultLabel,
    ...rest
  } = facet;

  const expanded: SizeFacetSplit[] = categories.map(cat => ({
    ...rest,
    queryParam: facet.queryParam,
    type: facet.type,
    label: cat.label,
    defaultLabel: cat.defaultLabel,
    parentCategoryKey: cat.parentCategoryKey,
    data: cat.data
  }));

  return [...facets.slice(0, idx), ...expanded, ...facets.slice(idx + 1)];
};

export const filterSwitcherTextVariants = {
  [FilterSizeVariants.SIMPLE]: "advanceFilterSwitch",
  [FilterSizeVariants.ADVANCED]: "simpleFilterSwitch"
};

export const resolveAdvancedData = (data: any) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray((data as any)?.advanced)) return (data as any).advanced;
  if (Array.isArray((data as any)?.simple)) return (data as any).simple;
  return [];
};

export const resolveSimpleData = (data: any) => {
  if (Array.isArray(data)) return getAllSizes({ data });
  if (Array.isArray((data as any)?.simple)) return (data as any).simple;
  if (Array.isArray((data as any)?.advanced)) {
    return getAllSizes({ data: (data as any).advanced });
  }
  return [];
};

const isSearchOrPageQueryKey = (queryParamKey: string): boolean =>
  queryParamKey === "q" || queryParamKey === "page";

const buildFacetFromUrlOptionKeys = (
  queryParamKey: string,
  optionKeysFromUrl: string[],
  facetFromListing: (Facet & { selectedOptions: FilterOption[] }) | undefined,
  facetFromPreviousSelection:
    | (Facet & {
        selectedOptions: FilterOption[];
      })
    | undefined
): Facet & { selectedOptions: FilterOption[] } => {
  const optionsByKeyFromListing = new Map(
    (facetFromListing?.selectedOptions ?? []).map(o => [String(o.key), o])
  );
  const optionsByKeyFromPrevious = new Map(
    (facetFromPreviousSelection?.selectedOptions ?? []).map(o => [
      String(o.key),
      o
    ])
  );

  const selectedOptions: FilterOption[] = optionKeysFromUrl.map(
    key =>
      optionsByKeyFromListing.get(key) ??
      optionsByKeyFromPrevious.get(key) ??
      ({ key } as FilterOption)
  );

  const facetBase = facetFromListing ?? facetFromPreviousSelection;
  return {
    ...(facetBase ?? {}),
    queryParam: queryParamKey,
    selectedOptions
  } as Facet & { selectedOptions: FilterOption[] };
};

const categoryQueryParamKeySet = new Set<string>(
  CATEGORY_QUERY_PARAMS as unknown as string[]
);

export const mergeSelectedFiltersFromUrlAndListing = (
  previousSelection: SelectedFiltersType,
  facetsFromListingResponse: SelectedFiltersType,
  selectedByQuery: Record<string, any>,
  _listingSelectedSizeKeys?: Set<string>
): SelectedFiltersType => {
  const merged: SelectedFiltersType = {};

  for (const queryParamKey of Object.keys(selectedByQuery)) {
    if (isSearchOrPageQueryKey(queryParamKey)) continue;

    const selectedOptionsFromUrl =
      selectedByQuery[queryParamKey]?.selectedOptions;
    if (
      !Array.isArray(selectedOptionsFromUrl) ||
      !selectedOptionsFromUrl.length
    )
      continue;

    let optionKeysFromUrl = selectedOptionsFromUrl.map((o: { key: string }) =>
      String(o.key)
    );

    const listingFacet = facetsFromListingResponse[queryParamKey];

    if (categoryQueryParamKeySet.has(queryParamKey)) {
      if (!listingFacet?.selectedOptions?.length) {
        continue;
      }
      const listingSelectedKeys = new Set(
        (listingFacet.selectedOptions ?? []).map((o: FilterOption) =>
          String(o.key)
        )
      );
      optionKeysFromUrl = optionKeysFromUrl.filter(k =>
        listingSelectedKeys.has(k)
      );
      if (optionKeysFromUrl.length === 0) continue;
    }

    merged[queryParamKey] = buildFacetFromUrlOptionKeys(
      queryParamKey,
      optionKeysFromUrl,
      listingFacet,
      previousSelection[queryParamKey]
    );
  }

  return merged;
};

export const normalizeQueryParamsForCompare = (
  obj: any
): Record<string, string[]> => {
  const normalized = Object.entries(obj || {}).reduce((acc, [k, v]) => {
    if (v === undefined || v === null) return acc;
    const arr = Array.isArray(v) ? v : [v];
    const normalizedArr = arr.map(x => String(x)).sort();
    if (normalizedArr.length === 0) return acc;
    acc[k] = normalizedArr;
    return acc;
  }, {} as Record<string, string[]>);
  return Object.keys(normalized)
    .sort()
    .reduce((acc, k) => {
      acc[k] = normalized[k];
      return acc;
    }, {} as Record<string, string[]>);
};

export const normalizeSelectedOptionsByQueryParam = (
  obj: any,
  ignoreQueryParams: string[] = ["q"]
): Record<string, string[]> => {
  const ignore = new Set(ignoreQueryParams);
  const normalized = Object.entries(obj || {}).reduce((acc, [k, v]) => {
    if (ignore.has(k)) return acc;
    const opts = (v as any)?.selectedOptions;
    if (!Array.isArray(opts) || opts.length === 0) return acc;
    acc[k] = opts.map((o: any) => String(o?.key)).sort();
    return acc;
  }, {} as Record<string, string[]>);
  return Object.keys(normalized)
    .sort()
    .reduce((acc, k) => {
      acc[k] = normalized[k];
      return acc;
    }, {} as Record<string, string[]>);
};

export const selectedFiltersFromSelectedByQuery = (
  selectedByQuery: Record<string, any>,
  ignoreQueryParams: string[] = ["q"]
): SelectedFiltersType => {
  const ignore = new Set(ignoreQueryParams);
  return Object.entries(selectedByQuery || {}).reduce((acc, [key, value]) => {
    if (ignore.has(key)) return acc;
    if (!value || typeof value !== "object") return acc;
    const opts = (value as any).selectedOptions;
    if (!Array.isArray(opts) || opts.length === 0) return acc;
    acc[key] = value as Facet & { selectedOptions: FilterOption[] };
    return acc;
  }, {} as SelectedFiltersType);
};

export const resolveTagToFilterKey = (
  tag: { filter: { queryParam?: string }; key: string },
  selectedFilters: Record<string, any>
): string | undefined => {
  const qp = tag.filter?.queryParam;
  if (qp) return qp;
  return Object.keys(selectedFilters || {}).find(k =>
    selectedFilters[k]?.selectedOptions?.some(
      (o: FilterOption) => o.key === tag.key
    )
  );
};

export const getFacetIdentity = (facet: any): string => {
  return [
    facet?.type || "",
    facet?.queryParam || "",
    facet?.parentCategoryKey || "",
    (facet?.defaultLabel || "").toLowerCase(),
    (facet?.label || "").toLowerCase()
  ].join("|");
};

export const buildFacetMapByIdentity = <TFacet = any>(
  facets: TFacet[]
): Map<string, TFacet> => {
  const map = new Map<string, TFacet>();
  (facets || []).forEach((f: any) => map.set(getFacetIdentity(f), f));
  return map;
};

const sortedOptionKeysFromSelection = (
  selectedOptions: FilterOption[] | undefined
): string[] => {
  if (!Array.isArray(selectedOptions) || selectedOptions.length === 0)
    return [];
  return selectedOptions.map(o => String(o?.key)).sort();
};

export const collectFilterQueryParamKeysFromFacets = (
  facets: FacetBase[]
): Set<string> => {
  const keys = new Set<string>();
  (facets || []).forEach((facet: any) => {
    if (!facet) return;
    if (facet.type === FILTER_TYPES.CATEGORY && Array.isArray(facet.data)) {
      const traverse = (nodes: any[]) => {
        nodes.forEach(node => {
          if (node?.queryParam) keys.add(node.queryParam);
          if (Array.isArray(node?.children)) traverse(node.children);
        });
      };
      traverse(facet.data);
    } else if (
      facet.type === FILTER_TYPES.PRICE &&
      typeof facet.queryParam === "string"
    ) {
      if (facet.queryParam.includes("&")) {
        const [minAttr, maxAttr] = facet.queryParam.split("&");
        const [minKey] = minAttr.split("=");
        const [maxKey] = maxAttr.split("=");
        if (minKey) keys.add(minKey);
        if (maxKey) keys.add(maxKey);
      } else if (facet.queryParam) {
        keys.add(facet.queryParam);
      }
    } else if (
      (facet.type as string) === FILTER_TYPES.MORE_FILTERS &&
      Array.isArray(facet.data)
    ) {
      facet.data.forEach((subItem: any) => {
        if (subItem?.queryParam) keys.add(subItem.queryParam);
      });
    } else if (facet.queryParam) {
      keys.add(facet.queryParam);
    }
  });
  return keys;
};

export const collectCategoryQueryParamKeysFromFacets = (
  facets: FacetBase[]
): Set<string> => {
  const keys = new Set<string>();
  (facets || []).forEach((facet: any) => {
    if (facet?.type !== FILTER_TYPES.CATEGORY || !Array.isArray(facet?.data))
      return;
    const traverse = (nodes: any[]) => {
      nodes.forEach(node => {
        if (node?.queryParam) keys.add(String(node.queryParam));
        if (Array.isArray(node?.children)) traverse(node.children);
      });
    };
    traverse(facet.data);
  });
  return keys;
};

export const collectMoreFilterQueryParamKeysFromFacets = (
  facets: FacetBase[]
): Set<string> => {
  const keys = new Set<string>();
  (facets || []).forEach((facet: any) => {
    if (facet?.type !== FILTER_TYPES.MORE_FILTERS || !Array.isArray(facet.data))
      return;
    facet.data.forEach((subItem: any) => {
      if (subItem?.queryParam) keys.add(subItem.queryParam);
    });
  });
  return keys;
};

export const listingApiSelectionFromSelectedFiltersData = (
  selectedFiltersData: unknown[] | null | undefined
): SelectedFiltersType => {
  const out: SelectedFiltersType = {};
  if (!Array.isArray(selectedFiltersData)) return out;

  for (const filter of selectedFiltersData as any[]) {
    if (!filter || typeof filter !== "object") continue;
    const type = filter.type;

    if (type === FILTER_TYPES.PRICE) {
      const data = filter.data;
      const sr = data?.selectedRange ?? data?.range;
      if (!sr || typeof filter.queryParam !== "string") continue;
      if (filter.queryParam.includes("&")) {
        const [minAttr, maxAttr] = filter.queryParam.split("&");
        const [minKey] = minAttr.split("=");
        const [maxKey] = maxAttr.split("=");
        if (minKey) {
          out[minKey] = {
            ...filter,
            queryParam: minKey,
            selectedOptions: [{ key: String(sr.min) } as FilterOption]
          };
        }
        if (maxKey) {
          out[maxKey] = {
            ...filter,
            queryParam: maxKey,
            selectedOptions: [{ key: String(sr.max) } as FilterOption]
          };
        }
      }
      continue;
    }

    if (type === FILTER_TYPES.CATEGORY && Array.isArray(filter.data)) {
      const selected = filter.data.filter((n: any) => {
        if (n?.queryParam == null || n?.key == null) return false;
        if ("isSelected" in n && n.isSelected !== true) return false;
        return true;
      });
      const grouped = groupBy(selected, (n: any) => String(n.queryParam));
      for (const [qp, opts] of Object.entries(grouped)) {
        out[qp] = {
          ...filter,
          queryParam: qp,
          selectedOptions: (opts as any[]).map(o => ({ ...o, key: o.key }))
        };
      }
      continue;
    }

    const qp = filter.queryParam;
    if (!qp || typeof qp !== "string") continue;
    const data = filter.data;
    if (!Array.isArray(data)) continue;
    const selectedOptions = data.filter(
      (o: any) =>
        o?.key != null && (o.isSelected === undefined || o.isSelected === true)
    ) as FilterOption[];
    if (selectedOptions.length === 0) continue;
    out[qp] = {
      ...filter,
      queryParam: qp,
      selectedOptions
    };
  }

  return out;
};

export const collectSizeKeysFromListingSelectedFiltersData = (
  selectedFiltersData: unknown[] | null | undefined
): Set<string> => {
  const keys = new Set<string>();
  if (!Array.isArray(selectedFiltersData)) return keys;
  const block = (selectedFiltersData as any[]).find(
    (f: any) =>
      f &&
      f.queryParam === FILTER_QUERY_PARAMS.SIZE &&
      f.type === FILTER_TYPES.SIZE
  );
  if (!block || !Array.isArray(block.data)) return keys;
  for (const row of block.data) {
    if (row?.key != null) keys.add(String(row.key));
  }
  return keys;
};

export const addPriceMinMaxFromUrlToFacets = (
  facetsFromListing: SelectedFiltersType,
  selectedFiltersData: unknown[] | null | undefined,
  selectedByQuery: Record<string, any>
): SelectedFiltersType => {
  const next: SelectedFiltersType = { ...facetsFromListing };
  const filtersList = (selectedFiltersData ?? []) as any[];
  const priceFilter = filtersList.find(f => f.type === FILTER_TYPES.PRICE);
  if (!priceFilter) return next;

  const minValue = selectedByQuery?.min?.selectedOptions?.[0]?.key;
  const maxValue = selectedByQuery?.max?.selectedOptions?.[0]?.key;

  if (minValue != null) {
    next.min = {
      ...priceFilter,
      queryParam: "min",
      selectedOptions: [{ key: String(minValue) } as FilterOption]
    };
  }
  if (maxValue != null) {
    next.max = {
      ...priceFilter,
      queryParam: "max",
      selectedOptions: [{ key: String(maxValue) } as FilterOption]
    };
  }
  return next;
};

export const buildFacetsForListingUrlMerge = (
  selectedFiltersData: unknown[] | null | undefined,
  selectedByQuery: Record<string, any>
): SelectedFiltersType => {
  const fromApi =
    listingApiSelectionFromSelectedFiltersData(selectedFiltersData);
  return addPriceMinMaxFromUrlToFacets(
    fromApi,
    selectedFiltersData,
    selectedByQuery
  );
};

export const getTouchedQueryParamsFromSelection = (
  selected: unknown,
  categoryQueryParamKeys: Set<string>
): Set<string> => {
  const touched = new Set<string>();
  if (selected == null) return touched;
  const list = Array.isArray(selected) ? selected : [selected];
  let categoryTouched = false;
  for (const item of list as any[]) {
    const qp = item?.queryParam;
    if (qp != null && categoryQueryParamKeys.has(String(qp))) {
      categoryTouched = true;
    } else if (qp != null) {
      touched.add(String(qp));
    }
  }
  if (categoryTouched) {
    categoryQueryParamKeys.forEach(k => touched.add(k));
  }
  return touched;
};

const applySelectionQueryParamKeysToParsedQuery = (
  next: Record<string, string | string[] | null | undefined>,
  selection: SelectedFiltersType,
  queryParamKeys: string[]
): void => {
  for (const key of queryParamKeys) {
    const facet = selection[key];
    if (!facet?.queryParam || !Array.isArray(facet.selectedOptions)) {
      delete next[key];
      continue;
    }
    const optionKeys = facet.selectedOptions.map(o => String(o.key));
    const qp = facet.queryParam;
    if (optionKeys.length === 0) {
      delete next[qp];
    } else {
      next[qp] = optionKeys;
    }
  }
};

/** Compare two in-memory PLP filter maps (same shape as URL merge): option keys per query param. */
export const arePlpFilterSelectionsEqualByParamKeys = (
  a: Record<string, any>,
  b: Record<string, any>,
  filterQueryParamKeys: Set<string>
): boolean => {
  for (const key of filterQueryParamKeys) {
    const sa = sortedOptionKeysFromSelection(a?.[key]?.selectedOptions);
    const sb = sortedOptionKeysFromSelection(b?.[key]?.selectedOptions);
    if (!isEqual(sa, sb)) return false;
  }
  return true;
};

export const areFilterSelectionsEqualToUrl = (
  selectedFilters: Record<string, any>,
  selectedByQuery: Record<string, any>,
  filterQueryParamKeys: Set<string>
): boolean =>
  arePlpFilterSelectionsEqualByParamKeys(
    selectedFilters,
    selectedByQuery,
    filterQueryParamKeys
  );

export const buildPlpQueryFromFilterSelection = (
  pathname: string,
  locationSearch: string,
  updatedSelection: SelectedFiltersType,
  options?: BuildPlpQueryFromFilterSelectionOptions | null
): string => {
  const parsed = qs.parse(locationSearch, { decode: false }) as Record<
    string,
    string | string[] | null | undefined
  >;

  const fk = options?.filterQueryParamKeys;
  const touched = options?.touchedQueryParamKeys ?? new Set<string>();
  const fullReplace = options?.fullFilterReplace ?? false;

  const next: Record<string, string | string[] | null | undefined> = {
    ...parsed
  };

  if (fk && fk.size > 0 && fullReplace) {
    for (const key of fk) {
      delete next[key];
    }
    applySelectionQueryParamKeysToParsedQuery(next, updatedSelection, [...fk]);
  } else if (fk && fk.size > 0 && touched.size > 0) {
    const touchedFilterKeys = [...touched].filter(
      k => fk.has(k) || updatedSelection[k] != null
    );
    applySelectionQueryParamKeysToParsedQuery(
      next,
      updatedSelection,
      touchedFilterKeys
    );
  } else if (!fk || fk.size === 0) {
    Object.values(updatedSelection).forEach(facet => {
      if (!facet?.queryParam || !Array.isArray(facet.selectedOptions)) return;
      const optionKeys = facet.selectedOptions.map(o => String(o.key));
      if (optionKeys.length === 0) {
        delete next[facet.queryParam];
      } else {
        next[facet.queryParam] = optionKeys;
      }
    });
  }

  delete next.page;

  return qs.stringifyUrl(
    { url: pathname, query: next },
    { skipNull: true, skipEmptyString: true, encode: false }
  );
};
